/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { InputField } from "@gfg/ui-v2/components/input";
import { Controller, useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";

import { useCallback } from "react";
import SearchConfigAttributeWithRulesFilter from "search-config/components/search-config-form/filters/search-config-attribute-with-rules";
import { AttributeFilterWithRuleFormValue } from "common/components/form-filters/attribute-filters-with-rules";
import SectionLabel from "common/components/recommendations/section-label";
import SectionTitle from "common/components/recommendations/section-title";
import preventSubmitOnEnter from "common/utils/form/prevent-submit-on-enter";
import FormHeader from "./form-header";
import useStyles from "./use-styles";
import campaignValidationSchema from "./validation-schema";
import HardBoostingInput from "../hard-boosting-input";

export interface FormData {
  campaignName: string;
  campaignId: string;
  rankAttributes: AttributeFilterWithRuleFormValue[];
  boostedSkus: string[];
}

export interface CampaignFormProps {
  onSubmit(formData: FormData): Promise<void>;
  onCancel(): void;
  onPreview?(): void;
  formTitle: string;
  defaultValues?: FormData;
  allowImmediateSubmit?: boolean;

  readOnly?: boolean;
}

export default function CampaignForm({
  onSubmit,
  onCancel,
  onPreview,
  formTitle,
  allowImmediateSubmit,
  defaultValues,
  readOnly = false,
}: CampaignFormProps) {
  const { classes, cx } = useStyles();
  const methods = useForm<FormData>({
    resolver: yupResolver(campaignValidationSchema),
    mode: "onChange",
    defaultValues: defaultValues ?? {
      campaignName: "",
      campaignId: "",
      rankAttributes: [],
      boostedSkus: [],
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset,
  } = methods;

  const handleSubmitAndResetForm: SubmitHandler<FormData> = useCallback(
    async formData => {
      await onSubmit(formData);
      reset({}, { keepValues: true });
    },
    [onSubmit, reset],
  );

  const isSubmitDisabled = readOnly || (allowImmediateSubmit ? !isValid : !isDirty || !isValid);
  return (
    <FormProvider {...methods}>
      <form onKeyDown={preventSubmitOnEnter} onSubmit={handleSubmit(handleSubmitAndResetForm)}>
        <FormHeader title={formTitle} onCancel={onCancel} onPreview={onPreview} disabledSubmit={isSubmitDisabled} />
        <div className={classes.generalSettings}>
          <SectionTitle
            title="Campaign Settings"
            description="Add campaign ranking rules to control the product ranking in campaign pages"
          />
          <div className={cx(classes.inputField, classes.textField, classes.verticallyMiddle)}>
            <SectionLabel
              title="Campaign name"
              description="Add a campaign name to identify the campaign. This is used for showing the campaign in Datajet portal. It will not be shown anywhere else and it is not used anywhere else. It doesn't have to be same as actual campaign name."
            />
            <Controller
              name="campaignName"
              control={control}
              render={({ field }) => (
                <InputField
                  {...field}
                  disabled={readOnly}
                  hint={errors.campaignName?.message}
                  placeholder="Campaign name"
                  block
                />
              )}
            />
          </div>
          <div className={cx(classes.inputField, classes.textField, classes.verticallyMiddle)}>
            <SectionLabel
              title="Campaign ID"
              description="Add campaign id of the campaign. This id is generated while creating campaign page in BOB."
            />
            <Controller
              name="campaignId"
              control={control}
              render={({ field }) => (
                <InputField
                  {...field}
                  disabled={readOnly}
                  hint={errors.campaignId?.message}
                  placeholder="Campaign ID"
                  block
                />
              )}
            />
          </div>
          <div>
            <SectionTitle
              title="Ranking Rules"
              description={
                <>
                  Add ranking rules to promote product on the campaign page. All the ranking criterias will be used for
                  ranking products on the campaign pages <br />
                  <a target="_blank" href="/docs/campaign">
                    Read more about the Ranking
                  </a>
                </>
              }
            />
            <SearchConfigAttributeWithRulesFilter
              addAttributeButtonLabel="Add Soft Boosting"
              disabled={readOnly}
              fieldName="rankAttributes"
              type="Ranking"
              excludeAttributes={["sku"]}
            />
          </div>
          <div>
            <SectionTitle
              title="Boosted SKUs"
              description={
                <>
                  Add the SKUs in comma separated format here. All the SKUs added here will be shown in the top of the
                  campaign page, in the same order they are added here
                </>
              }
            />
            <Controller
              name="boostedSkus"
              control={control}
              render={({ field }) => <HardBoostingInput onChange={field.onChange} skus={field.value} />}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
